import { useTranslation } from 'react-i18next';
import {
  TripFilters,
  TripList,
  useDays,
  useListMode,
  useSearchHotels,
  useSelectedDay,
  useSelectedType,
  useTripFilters,
  useTripStore,
  getExperienceCompatibility,
} from '@koob/trip-designer';
import { useApp, useLanguage } from '../../../hooks';
import ComposeHeader from './ComposeHeader';
import ChatStatusFilter from './ChatStatusFilter';
import ExperienceCard from 'components/Experience/ExperienceCard';
import ComposeChatTrip from '../Chat/ComposeChatTrip';
import ComposeMap from './Map/ComposeMap';
import { useTripHotelCardProps } from './Map/tripHotelCard';
import ComposeOverlapModal from './ComposeOverlapModal';
import { useAddToTrip } from './useAddToTrip';
import { useComposeSearch } from './useComposeSearch';
import { useComposeTrip } from './useComposeTrip';
import { useHotelConflictNotice } from './useHotelConflictNotice';

/**
 * The composer: loads the trip, searches the catalog for the selected day, and
 * lays the shared list and map out with TO's slots (filters, chat).
 */
export default function ComposeScreen({ tripId }) {
  const { t } = useTranslation('tripDesigner');
  const { lang } = useLanguage();
  const { currentOrganization } = useApp();
  const tripFilters = useTripFilters();
  const listMode = useListMode();
  const searchHotels = useSearchHotels();
  const tripHotelCardProps = useTripHotelCardProps();
  const days = useDays();
  const selectedDay = useSelectedDay();
  const selectedType = useSelectedType();
  const experiencesMap = useTripStore(state => state.experiences);

  const { trip, reloadTrip, versions, currentVersion, hasHydrated } =
    useComposeTrip({ tripId });
  const {
    experiencesList,
    hotels,
    hotelPage,
    setHotelPage,
    hotelPerPage,
    setHotelPerPage,
    hotelTotalPages,
    hotelTotalCount,
    areProgramsLoading,
    isLoading,
  } = useComposeSearch({ tripId });
  const {
    addHotel,
    addExperience,
    overlapingExperience,
    cancelOverlap,
    confirmAddExperience,
  } = useAddToTrip();
  useHotelConflictNotice();

  const compatibilityOf = experience =>
    getExperienceCompatibility({
      experience,
      days,
      selectedDay,
      selectedType,
      experiencesMap,
    });

  const tripCountries = (trip?.countries || []).map(c => c.id);

  const filters = (
    <TripFilters
      t={t}
      locale={lang}
      organizationId={currentOrganization?.organizationId}
      countries={tripCountries}
      transitionPrograms
      chatFilters={<ChatStatusFilter />}
      header={
        <ComposeHeader
          tripId={tripId}
          versions={versions}
          currentVersion={currentVersion}
          reloadTrip={reloadTrip}
        />
      }
    />
  );

  return (
    <>
      {/* KD-3391: compose column on the left; the filters stack above the
          list/map/itinerary panel in the right column. Chat mode keeps
          TripsFilter mounted standalone for the navbar trip-header portal. */}
      {listMode === 'chat' && filters}

      {listMode !== 'chat' && (
        <ComposeMap
          filterSlot={filters}
          experiences={experiencesList}
          hotels={hotels}
          addExperience={addExperience}
          addHotel={addHotel}
          tripId={tripId}
          hasHydrated={hasHydrated}
          getExperienceCompatibility={compatibilityOf}
          reloadTrip={reloadTrip}
          itineraryMode={listMode === 'itinerary'}
          areProgramsLoading={areProgramsLoading}
          trip={trip}
          listSlot={
            <TripList
              experiences={experiencesList}
              hotels={hotels}
              addExperience={addExperience}
              addHotel={addHotel}
              isLoading={isLoading}
              getExperienceCompatibility={compatibilityOf}
              page={hotelPage}
              setPage={setHotelPage}
              totalPages={hotelTotalPages}
              totalCount={hotelTotalCount}
              perPage={hotelPerPage}
              setPerPage={setHotelPerPage}
              locale={lang ?? tripFilters?.locale ?? 'en'}
              isForTrip
              tripHotelCardProps={tripHotelCardProps}
              resultsText={
                !isLoading
                  ? hotels?.length > 0 || experiencesList?.length > 0
                    ? t('experiences:results', {
                        count: searchHotels
                          ? (hotelTotalCount ?? hotels.length)
                          : experiencesList?.length,
                      })
                    : t('experiences:noResult', {
                        product: searchHotels ? 'hotel' : 'experience',
                      })
                  : null
              }
              renderExperienceCard={(
                experience,
                { compatibility, onSelect },
              ) => (
                <ExperienceCard
                  experience={experience}
                  mode="compose"
                  compatibility={compatibility}
                  isForTrip={true}
                  onSelect={onSelect}
                  locale={lang ?? tripFilters?.locale ?? 'en'}
                />
              )}
            />
          }
        />
      )}

      {listMode === 'chat' && (
        <ComposeChatTrip tripId={tripId} reloadTrip={reloadTrip} trip={trip} />
      )}

      <ComposeOverlapModal
        overlapingExperience={overlapingExperience}
        onCancel={cancelOverlap}
        onConfirm={confirmAddExperience}
      />
    </>
  );
}
